import './HRecommendedItems.scss';

import * as tsx from 'vue-tsx-support';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import { ResolvedLanugageData } from '~/server-middleware/data-server/adapter/ACCOAdapter/schemes';
import { AvailableLanguage, RecommendedItemsModule } from '~/schemes';
import { HImg } from '~/components/HImg';
import { HIcon } from '~/components/HIcon';
import { HLink } from '~/components/HLink';

export interface HRecommendedItemsProps {
  value: ResolvedLanugageData<
    RecommendedItemsModule['properties'],
    AvailableLanguage
  >;
}

export interface HRecommendedItemsEmits {}

export interface HRecommendedItemsScopedSlots {}

@Component<HRecommendedItemsRef>({
  name: 'HRecommendedItems',

  mounted() {
    this.handleScreenChange();
    window.addEventListener('resize', this.handleScreenChange);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleScreenChange);
  },
  render() {
    return (
      <div class="h-container h-recommended-items">
        <h2 class="h-recommended-items__title">{this.value.title}</h2>
        <div class="h-recommended-items__contents">
          {this.isPC && (
            <button
              class="h-recommended-items__list-arrow h-recommended-items__list-arrow-left"
              on={{ click: this.handleScrollLeft }}>
              <HIcon
                staticClass="h-recommended-items__list-arrow-icon"
                name="keyboard-arrow-left"
              />
            </button>
          )}
          <div class="h-recommended-items__list">
            {this.value.items.map((item) => (
              <div class="h-recommended-items__item">
                <HImg
                  alt={item.name || item.description}
                  staticClass="h-recommended-items__item-image"
                  src={
                    item.image && item.image.derived && item.image.derived.main
                      ? item.image.derived.main.url
                      : ''
                  }
                  corner
                />
                <div class="h-recommended-items__item-content">
                  <div class="h-recommended-items__item-details">
                    <h3 class="h-recommended-items__item-name">{item.name}</h3>
                    <p class="h-recommended-items__item-description">
                      {item.description}
                    </p>
                  </div>
                  <p class="h-recommended-items__item-price">{item.price}</p>
                  {item.link && item.link.url && (
                    <div class="h-recommended-items__item-link">
                      <HLink
                        staticClass="h-recommended-items__item-link-text"
                        href={item.link.url}
                        target={item.link.blank ? '_blank' : '_self'}
                        rel={
                          item.link.blank ? 'noopener noreferrer' : undefined
                        }>
                        {item.link.text || '詳細'}
                      </HLink>
                      {item.link.blank && (
                        <HIcon
                          staticClass="h-recommended-items__item-link-icon"
                          name="open-in-new"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {this.isPC && (
            <button
              class="h-recommended-items__list-arrow h-recommended-items__list-arrow-right"
              on={{ click: this.handleScrollRight }}>
              <HIcon
                staticClass="h-recommended-items__list-arrow-icon"
                name="keyboard-arrow-right"
              />
            </button>
          )}
        </div>
      </div>
    );
  },
})
export class HRecommendedItemsRef
  extends Vue
  implements HRecommendedItemsProps {
  @Prop({ type: Object, required: true })
  readonly value!: ResolvedLanugageData<
    RecommendedItemsModule['properties'],
    AvailableLanguage
  >;

  // Observerを管理するための配列
  private observers: IntersectionObserver[] = [];

  get isPC() {
    return this.$mq.match.wide;
  }

  handleScreenChange() {
    if (this.isPC) {
      this.setupIntersectionObserver();
    } else {
      // SP幅の場合はObserverを停止
      this.cleanupObservers();
    }
  }

  /**
   * IntersectionObserverをクリーンアップする処理
   */
  cleanupObservers() {
    // 登録済みのObserverを全て停止
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];

    // 矢印を非表示に（SP表示では使用しないため）
    const leftArrow = this.$el.querySelector(
      '.h-recommended-items__list-arrow-left',
    );
    const rightArrow = this.$el.querySelector(
      '.h-recommended-items__list-arrow-right',
    );

    if (leftArrow instanceof HTMLElement) {
      leftArrow.style.display = 'none';
    }

    if (rightArrow instanceof HTMLElement) {
      rightArrow.style.display = 'none';
    }
  }

  /**
   * 左方向（対象を左から右へ動かす）へスクロールする処理
   */
  handleScrollLeft(event: Event) {
    event.preventDefault();
    const list = this.$el.querySelector('.h-recommended-items__list');
    if (list instanceof HTMLElement) {
      list.scrollTo({
        left: list.scrollLeft - 240,
        behavior: 'smooth',
      });
    }
  }

  /**
   * 右方向（対象を右から左へ動かす）へスクロールする処理
   */
  handleScrollRight(event: Event) {
    event.preventDefault();
    const list = this.$el.querySelector('.h-recommended-items__list');
    if (list instanceof HTMLElement) {
      list.scrollTo({
        left: list.scrollLeft + 240,
        behavior: 'smooth',
      });
    }
  }

  /**
   * IntersectionObserverを設定して、最初のアイテムが見えるときに左矢印を、
   * 最後のアイテムが見えるときに右矢印を非表示にする処理
   */
  setupIntersectionObserver() {
    const list = this.$el.querySelector('.h-recommended-items__list');
    const firstItem = this.$el.querySelector(
      '.h-recommended-items__item:first-child',
    );
    const lastItem = this.$el.querySelector(
      '.h-recommended-items__item:last-child',
    );
    const leftArrow = this.$el.querySelector(
      '.h-recommended-items__list-arrow-left',
    );
    const rightArrow = this.$el.querySelector(
      '.h-recommended-items__list-arrow-right',
    );

    // 左矢印の処理
    if (list && firstItem && leftArrow instanceof HTMLElement) {
      // 最初のアイテムを監視するObserverを作成
      const firstItemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 最初のアイテムが表示されているとき、左矢印を非表示にする
            leftArrow.style.display = !entry.isIntersecting ? 'flex' : 'none';
          });
        },
        {
          root: list,
          threshold: 0.5, // 閾値: アイテムの50%が見えたとき
          rootMargin: '0px',
        },
      );

      firstItemObserver.observe(firstItem);
      this.observers.push(firstItemObserver); // 後でクリーンアップできるように保存

      // 初期状態では左矢印を非表示にする
      leftArrow.style.display = 'none';
    }

    // 右矢印の処理
    if (list && lastItem && rightArrow instanceof HTMLElement) {
      // 最後のアイテムを監視するObserverを作成
      const lastItemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // 最後のアイテムが表示されているとき、右矢印を非表示にする
            rightArrow.style.display = !entry.isIntersecting ? 'flex' : 'none';
          });
        },
        {
          root: list,
          threshold: 0.5, // 閾値: アイテムの50%が見えたとき
          rootMargin: '0px',
        },
      );

      lastItemObserver.observe(lastItem);
      this.observers.push(lastItemObserver); // 後でクリーンアップできるように保存

      if (list instanceof HTMLElement) {
        // スクロールが可能かどうかを確認（コンテンツの幅がコンテナより大きいか）
        const canScroll = list.scrollWidth > list.clientWidth;
        // スクロールできない場合や、最後の要素が最初から見えている場合は右矢印を非表示に
        rightArrow.style.display = canScroll ? 'flex' : 'none';
      }
    }
  }
}

export const HRecommendedItems = tsx
  .ofType<
    HRecommendedItemsProps,
    HRecommendedItemsEmits,
    HRecommendedItemsScopedSlots
  >()
  .convert(HRecommendedItemsRef);
